import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useUpdateGnrSupplierMutation } from '../../../../hooks/mutations/useGnrSupplierMutation';
import { useGnrSuppliersQuery } from '../../../../hooks/queries/useGnrSuppliersQuery';
import { useAuth } from '../../../../hooks/useAuth';
import { GnrSupplier } from '../../../../services/apiSLTP/sites/gnrSuppliers';
import { capitalizeFirstLetter } from '../../../../utils/capitalize';
import { getStatusColor } from '../../../../utils/colors';
import { crudStatus, getCrudStatusInfos, localStorageKeys, permissionLevels } from '../../../../utils/enums';
import StateCell from '../../../cells/StateCell';
import GenericAgGridTable from '../../GenericAgGridTable';
import Icon from '../../../Icon';
import IconLine from '../../../IconLine';
import UpdateGnrSupplierModal from '../../../modals/admin/external/UpdateGnrSupplierModal';
import DeleteModal from '../../../modals/DeleteModal';
import { ColDef, ICellRendererParams } from 'ag-grid-community';
import IconButton from '../../../shared/IconButton';
import { createDateColumn, createDateTimeColumn } from '../../../../utils/dates';

interface GnrSuppliersTableProps {
    withUsageCount: boolean;
}

const GnrSuppliersTable: React.FC<GnrSuppliersTableProps> = ({ withUsageCount }) => {
    const { t } = useTranslation();
    const { user } = useAuth();
    const {
        data: gnrSuppliers,
        isLoading: isGnrSuppliersLoading,
        isError: isGnrSuppliersError,
        isSuccess: isGnrSuppliersSuccess,
        refetch: refetchGnrSuppliers,
    } = useGnrSuppliersQuery({ queries: { withUsage: withUsageCount } });

    const [showEditModal, setShowEditModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [selectedGnrSupplier, setSelectedGnrSupplier] = useState<GnrSupplier | null>(null);
    const [isEditMode, setIsEditMode] = useState(false);

    const {
        mutate: mutateGnrSupplier,
        isSuccess: isSuccessGnrSupplier,
        isError: isErrorGnrSupplier,
        isLoading: isLoadingGnrSupplier,
        error: errorGnrSupplier,
        data: dataGnrSupplier,
    } = useUpdateGnrSupplierMutation({
        onSuccess: (data, variables) => {
            refetchGnrSuppliers();
        },
        onError: (error) => {
            console.error('Error updating gnrSupplier:', error.message);
        },
    });

    const columnDefs: ColDef<GnrSupplier>[] = [
        { headerName: capitalizeFirstLetter(t('name')), field: 'name', initialFlex: 1, minWidth: 400, colId: 'name' },
        { headerName: capitalizeFirstLetter(t('codeProC')), field: 'codeProC', colId: 'codeProC', initialWidth: 150 },
        {
            headerName: capitalizeFirstLetter(t('actions')),
            cellRenderer: (params: ICellRendererParams<GnrSupplier>) => {
                const data = params.data;
                if (!data) return null;
                return (
                    <IconLine>
                        <IconButton
                            iconName="edit"
                            title={capitalizeFirstLetter(t('modifyGnrSupplier'))}
                            onClick={() => {
                                setSelectedGnrSupplier(data);
                                setIsEditMode(true);
                                setShowEditModal(true);
                            }}
                            disabled={data.crudStatus === crudStatus.DELETED}
                        />
                        {user && user.profileId.permsLevel <= permissionLevels.ADMIN && (
                            <IconButton
                                iconName="delete"
                                title={capitalizeFirstLetter(t('deleteGnrSupplier'))}
                                onClick={() => {
                                    setSelectedGnrSupplier(data);
                                    setShowDeleteModal(true);
                                }}
                                disabled={data.crudStatus === crudStatus.DELETED}
                            />
                        )}
                    </IconLine>
                );
            },
            colId: 'actions',
            filter: false,
            enableRowGroup: false,
            enableValue: false,
            sortable: false,
        },
        {
            headerName: capitalizeFirstLetter(t('status')),
            valueGetter: (params) => capitalizeFirstLetter(t(getCrudStatusInfos(params.data?.crudStatus))),
            cellRenderer: (params: ICellRendererParams<GnrSupplier>) => {
                if (!params.data) return params.value;
                return <StateCell stateColor={getStatusColor(params.data?.crudStatus)} text={t(getCrudStatusInfos(params.data?.crudStatus))} />;
            },
            colId: 'status',
        },
        { headerName: capitalizeFirstLetter(t('area')), field: 'area', colId: 'area' },
        { headerName: capitalizeFirstLetter(t('email')), field: 'email', colId: 'email' },
        { headerName: capitalizeFirstLetter(t('mobile')), field: 'phone', colId: 'mobile' },
        createDateTimeColumn<GnrSupplier>('createdAt', 'createdAt'),
        createDateTimeColumn<GnrSupplier>('updatedAt', 'updatedAt'),
        ...((user?.profileId.permsLevel === permissionLevels.ADMIN
            ? [
                  {
                      headerName: capitalizeFirstLetter(t('_id')),
                      field: '_id',
                      colId: '_id',
                      cellClass: 'usage-col-main',
                      headerClass: 'usage-col-main',
                      initialWidth: 250,
                  },
                  {
                      headerName: capitalizeFirstLetter(t('xTotal')),
                      field: 'usageCount.total',
                      colId: 'xtotal',
                      cellClass: 'usage-col',
                      headerClass: 'usage-col',
                      filter: 'agNumberColumnFilter',
                  },
                  {
                      headerName: capitalizeFirstLetter(t('xGnrRequests')),
                      field: 'usageCount.gnrRequests',
                      colId: 'xgnrrequests',
                      cellClass: 'usage-col',
                      headerClass: 'usage-col',
                      filter: 'agNumberColumnFilter',
                  },
              ]
            : []) as ColDef<GnrSupplier>[]),
    ];

    const sortedGnrSuppliers = useMemo(() => {
        return gnrSuppliers?.data.sort((a, b) => a.name.localeCompare(b.name)) || [];
    }, [gnrSuppliers]);

    return (
        <>
            <GenericAgGridTable
                rowData={sortedGnrSuppliers}
                columnDefs={columnDefs}
                loading={isGnrSuppliersLoading}
                exportCsvFileName="gnrSuppliers"
                headerRightComponent={
                    <IconButton
                        iconName="addCircle"
                        onClick={() => {
                            setSelectedGnrSupplier(null);
                            setIsEditMode(false);
                            setShowEditModal(true);
                        }}
                        title={capitalizeFirstLetter(t('addGnrSupplier'))}
                    />
                }
                storageKey={localStorageKeys.TABLE.ADMIN.EXTERNAL.GNRSUPPLIERS}
                agGridProps={{ getRowId: ({ data }) => data._id }}
            />

            {showEditModal && (
                <UpdateGnrSupplierModal
                    showEditModal={showEditModal}
                    setShowEditModal={setShowEditModal}
                    gnrSupplier={selectedGnrSupplier}
                    isEditMode={isEditMode}
                    refetch={refetchGnrSuppliers}
                />
            )}
            {showDeleteModal && selectedGnrSupplier && (
                <DeleteModal
                    open={showDeleteModal}
                    handleClose={() => setShowDeleteModal(false)}
                    deleteAction={() => {
                        mutateGnrSupplier({ gnrSupplierId: selectedGnrSupplier._id, data: { crudStatus: crudStatus.DELETED } });
                    }}
                    title={t('gnrSupplier')}
                    itemName={selectedGnrSupplier.name}
                />
            )}
        </>
    );
};

export default GnrSuppliersTable;
